import { useEffect, useMemo, useRef, useCallback } from 'react'
import { useClientMethod, useHub, useHubMethod } from 'react-use-signalr'
import { buildFleetDataHub } from './signalRHubs'

const FleetDataSignalR = ({
  token,
  companyId,
  fleetType,
  licenseIds,
  selectedUnitUId,
  selectedLicenseId,
  chartRequest,
  hubActionsRef,
  onSubscribed,
  onIncomingData,
  onDetailData,
  onChartData,
  onConnectionError,
}) => {
  const subscribeKey = useMemo(() => {
    if (!fleetType || !licenseIds?.length) return null
    return JSON.stringify({ fleetType, licenseIds })
  }, [fleetType, licenseIds])

  const requestBody = useMemo(() => {
    if (!subscribeKey) return null
    return {
      fleetType,
      licenseIds: licenseIds.map(String),
    }
  }, [subscribeKey, fleetType, licenseIds])

  const chartSubscribeKey = useMemo(() => {
    if (!chartRequest?.standardChannelName || !chartRequest?.dateType) return null
    if (!fleetType || !licenseIds?.length) return null
    return JSON.stringify({
      fleetType,
      licenseIds: licenseIds.map(String),
      standardChannelName: chartRequest.standardChannelName,
      dateType: chartRequest.dateType,
    })
  }, [chartRequest, fleetType, licenseIds])

  const chartRequestBody = useMemo(() => {
    if (!chartSubscribeKey) return null
    return {
      fleetType,
      licenseIds: licenseIds.map(String),
      standardChannelName: chartRequest.standardChannelName,
      dateType: chartRequest.dateType,
    }
  }, [chartSubscribeKey, fleetType, licenseIds, chartRequest])

  const tokenRef = useRef(token)
  useEffect(() => {
    tokenRef.current = token
  }, [token])

  const hasToken = Boolean(token)
  const signalRConnection = useMemo(() => {
    if (!hasToken) return null
    return buildFleetDataHub(() => tokenRef.current, companyId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken, companyId])

  const { hubConnectionState } = useHub(signalRConnection)
  const { invoke } = useHubMethod(signalRConnection, 'SubscribeToFleetData')
  const { invoke: invokeDetail } = useHubMethod(signalRConnection, 'SubscribeToFleetDataDetailed')
  const { invoke: invokeUnsubDetail } = useHubMethod(signalRConnection, 'UnsubscribeFromFleetDataDetailed')
  const { invoke: invokeChart } = useHubMethod(signalRConnection, 'SubscribeToFleetChartData')
  const { invoke: invokeUnsubChart } = useHubMethod(signalRConnection, 'UnsubscribeFromFleetChartData')
  const { invoke: invokeAddCustom } = useHubMethod(signalRConnection, 'AddFleetCustomChannel')
  const { invoke: invokeRemoveCustom } = useHubMethod(signalRConnection, 'RemoveFleetCustomChannel')
  const lastInvokeKey = useRef(null)
  const lastDetailUnitUId = useRef(null)
  const lastChartKey = useRef(null)

  const handleIncomingData = useCallback((payload) => {
    // eslint-disable-next-line no-console
    console.log('%c[FleetData] incoming FleetDataResponse', 'color:#46d39a;font-weight:bold', payload)
    if (onIncomingData) onIncomingData(payload)
  }, [onIncomingData])
  useClientMethod(signalRConnection, 'FleetDataResponse', handleIncomingData)

  const handleDetailData = useCallback((payload) => {
    if (onDetailData) onDetailData(payload)
  }, [onDetailData])
  useClientMethod(signalRConnection, 'FleetUnitDetailResponse', handleDetailData)

  const handleChartData = useCallback((payload) => {
    // eslint-disable-next-line no-console
    console.log('%c[FleetChart] incoming FleetChartDataResponse', 'color:#00b7c3;font-weight:bold', payload)
    if (onChartData) onChartData(payload)
  }, [onChartData])
  useClientMethod(signalRConnection, 'FleetChartDataResponse', handleChartData)

  // Dashboard subscription
  useEffect(() => {
    if (!requestBody || hubConnectionState !== 'Connected' || !invoke) return undefined

    const key = subscribeKey
    if (lastInvokeKey.current === key) return undefined
    lastInvokeKey.current = key

    // Every new subscription starts from the configured channel mappings — overrides are not restored
    invoke(requestBody)
      .then(() => {
        if (onSubscribed) onSubscribed()
      })
      .catch((error) => {
        lastInvokeKey.current = null
        if (onConnectionError) onConnectionError(error)
      })

    return undefined
  }, [requestBody, invoke, hubConnectionState, subscribeKey, onSubscribed, onConnectionError])

  // Re-subscribe after reconnect
  useEffect(() => {
    if (hubConnectionState !== 'Connected') {
      lastInvokeKey.current = null
      lastDetailUnitUId.current = null
      lastChartKey.current = null
    }
  }, [hubConnectionState])

  // Field channel overrides (session only; applied by the server on the next tick)
  useEffect(() => {
    if (!hubActionsRef) return undefined
    const ensureConnected = () => {
      if (hubConnectionState !== 'Connected') return Promise.reject(new Error('Fleet connection is not ready'))
      return Promise.resolve()
    }
    hubActionsRef.current = {
      setFieldChannel: (unitUId, fieldName, channelId) => ensureConnected()
        .then(() => invokeAddCustom({ unitUId, fieldName, channelId })),
      resetFieldChannel: (unitUId, fieldName) => ensureConnected()
        .then(() => invokeRemoveCustom({ unitUId, fieldName })),
    }
    return () => { hubActionsRef.current = null }
  }, [hubActionsRef, hubConnectionState, invokeAddCustom, invokeRemoveCustom])

  // Unit detail subscription — server auto-cancels previous detail stream
  useEffect(() => {
    if (hubConnectionState !== 'Connected') return

    const prevUnitUId = lastDetailUnitUId.current
    if (prevUnitUId === selectedUnitUId) return

    lastDetailUnitUId.current = selectedUnitUId

    if (selectedUnitUId && selectedLicenseId && invokeDetail) {
      invokeDetail({ fleetType, licenseId: selectedLicenseId, unitUId: selectedUnitUId }).catch((error) => {
        lastDetailUnitUId.current = null
        if (onConnectionError) onConnectionError(error)
      })
    } else if (!selectedUnitUId && prevUnitUId && invokeUnsubDetail) {
      invokeUnsubDetail().catch(() => {})
    }
  }, [selectedUnitUId, selectedLicenseId, hubConnectionState, invokeDetail, invokeUnsubDetail, fleetType, onConnectionError])

  // Fleet chart historical + live stream
  useEffect(() => {
    if (hubConnectionState !== 'Connected') return undefined
    if (!invokeChart || !invokeUnsubChart) {
      // eslint-disable-next-line no-console
      console.log('[FleetChart] waiting for hub methods', {
        connected: hubConnectionState === 'Connected',
        hasInvokeChart: Boolean(invokeChart),
        hasUnsubChart: Boolean(invokeUnsubChart),
        chartSubscribeKey,
      })
      return undefined
    }

    const nextKey = chartSubscribeKey
    const prevKey = lastChartKey.current

    if (prevKey === nextKey) return undefined

    let cancelled = false

    const switchChart = async () => {
      // eslint-disable-next-line no-console
      console.log('[FleetChart] switching subscription', { prevKey, nextKey, chartRequestBody })

      if (prevKey && invokeUnsubChart) {
        try {
          // eslint-disable-next-line no-console
          console.log('%c[FleetChart] UnsubscribeFromFleetChartData', 'color:#f5845a;font-weight:bold')
          await invokeUnsubChart()
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('[FleetChart] UnsubscribeFromFleetChartData failed', error)
        }
      }

      if (cancelled) return

      if (nextKey && chartRequestBody) {
        try {
          // eslint-disable-next-line no-console
          console.log('%c[FleetChart] SubscribeToFleetChartData', 'color:#00b7c3;font-weight:bold', chartRequestBody)
          await invokeChart(chartRequestBody)
          if (cancelled) {
            try {
              await invokeUnsubChart()
            } catch (_) {
              /* ignore */
            }
            return
          }
          lastChartKey.current = nextKey
          // eslint-disable-next-line no-console
          console.log('%c[FleetChart] subscribe OK — waiting for FleetChartDataResponse…', 'color:#009513;font-weight:bold')
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[FleetChart] SubscribeToFleetChartData failed', error)
          if (!cancelled) {
            lastChartKey.current = null
            if (onConnectionError) onConnectionError(error)
          }
        }
      } else {
        lastChartKey.current = null
        // eslint-disable-next-line no-console
        console.log('[FleetChart] chart subscription cleared')
      }
    }

    switchChart()

    return () => {
      cancelled = true
    }
  }, [
    chartSubscribeKey,
    chartRequestBody,
    hubConnectionState,
    invokeChart,
    invokeUnsubChart,
    onConnectionError,
  ])
  
  useEffect(() => () => {
    if (lastChartKey.current && invokeUnsubChart) {
      invokeUnsubChart().catch(() => {})
      lastChartKey.current = null
    }
  }, [invokeUnsubChart])

  return null
}

export default FleetDataSignalR
